import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@azure/storage-blob';
import { LinearProgress } from '@mui/material';
import * as Ably from 'ably';
import { AblyProvider } from 'ably/react';

import { NotificationApi, UserApi } from '@/api';
import { ReleaseNotePopup } from '@/components/Modals/ReleaseNotesModal/ReleaseNotesModal';
import { ApiVersion, baseURL } from '@/config';
import { useAuthContext } from '@/hooks';
import { useReleaseNotesModal } from '@/hooks/useReleaseNotesModal';
import { LocationTracker } from '@/LocationTracker';
import { INotification } from '@/models';
import { createNotificationService } from '@/services/service.notification';
import { useNotificationStore, useUserStore } from '@/store';

import { LocalStorageItems } from './constants';
import { AppRoutes } from './routes';

function AblyWrapper() {
  const { accessToken } = useAuthContext();
  const { userAgencyId, user, account } = useUserStore();
  const { addNotification, setNotifications } = useNotificationStore();
  const [isLoading, setIsLoading] = useState(true);
  const { shouldShowPopup, latestRelease, setShouldShowPopup } =
    useReleaseNotesModal();

  const ablyRef = useRef<Ably.Types.RealtimePromise | undefined>(undefined);

  const setAgencyId = () => {
    const pathSegments = window?.location?.pathname?.split('/');
    const agencyIdIndex = pathSegments?.indexOf('agencies') + 1;
    const agencyId = pathSegments[agencyIdIndex];
    if (agencyId) {
      localStorage.setItem(LocalStorageItems.CurrentAgencyId, agencyId);
    } else {
      localStorage.setItem(
        LocalStorageItems.CurrentAgencyId,
        userAgencyId || '',
      );
    }
  };

  // Fetch initial notifications
  const fetchInitialNotifications = useCallback(async () => {
    try {
      const response = await NotificationApi.getNotifications({
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 50,
      });
      setNotifications(response.data.data);
    } catch (error) {
      logger.error('Failed to fetch initial notifications:', error);
    }
  }, [setNotifications]);

  // Initialize notification service when Ably is connected
  useEffect(() => {
    if (ablyRef.current && user?._id && account?._id) {
      fetchInitialNotifications();

      const notificationService = createNotificationService({
        accountId: account._id,
        userId: user._id,
        onNotificationReceived: (notification: INotification) => {
          const processedNotification = {
            ...notification,
            _id:
              notification._id ||
              `${notification.recordType}-${
                notification.recordId
              }-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          };

          const { notifications } = useNotificationStore.getState();

          const exists = notifications.some((n) => {
            if (n._id && processedNotification._id && n._id !== 'undefined') {
              return n._id === processedNotification._id;
            }

            const isSameContent =
              n.message === processedNotification.message &&
              n.recordType === processedNotification.recordType &&
              n.recordId === processedNotification.recordId;

            if (
              isSameContent &&
              n.createdAt &&
              processedNotification.createdAt
            ) {
              const timeDiff = Math.abs(
                new Date(n.createdAt).getTime() -
                  new Date(processedNotification.createdAt).getTime(),
              );
              return timeDiff < 500;
            }

            return false;
          });

          if (!exists) {
            addNotification(processedNotification);
          }
        },
        pollingInterval: 30000,
      });

      notificationService
        .initialize(ablyRef.current)
        .then(() => {
          logger.info('Notification service initialized successfully');
        })
        .catch((error) => {
          logger.error('Failed to initialize notification service:', error);
        });

      return () => {
        notificationService.destroy();
      };
    }
  }, [
    ablyRef.current,
    user?._id,
    account?._id,
    addNotification,
    fetchInitialNotifications,
  ]);

  useEffect(() => {
    if (accessToken && userAgencyId) {
      if (ablyRef.current) {
        ablyRef.current.close();
      }

      ablyRef.current = new Ably.Realtime.Promise({
        authHeaders: {
          Authorization: `Bearer ${accessToken}`,
          'X-HELPTAPP-AGENCY': userAgencyId || '',
        },
        authUrl: `${baseURL}api/${ApiVersion.V1}/users/ably-token-request`,
        authMethod: 'POST',
        authCallback: async (_, callback) => {
          try {
            setAgencyId();
            const tokenRequest = await UserApi.getAblyToken();
            callback(null, tokenRequest.data);
          } catch (err) {
            logger.error('Failed to get Ably token:', err);
            callback(err as string, null);
          }
        },
        echoMessages: false,
      });

      ablyRef.current.connection.once('connected', () => {
        setIsLoading(false);
      });

      ablyRef.current.connection.on('failed', (stateChange) => {
        if (stateChange.reason?.statusCode === 403) {
          logger.error('Ably connection forbidden:', stateChange.reason);
          setIsLoading(false);
        }
      });
    }

    return () => {
      if (ablyRef.current) {
        ablyRef.current.close();
        ablyRef.current = undefined;
      }
    };
  }, [accessToken, userAgencyId]);

  if (!accessToken || !userAgencyId) {
    return <AppRoutes />;
  }

  if (isLoading) {
    return <LinearProgress color="error" variant="indeterminate" />;
  }

  if (!ablyRef.current) {
    return <AppRoutes />;
  }

  return (
    <AblyProvider client={ablyRef.current}>
      {shouldShowPopup && latestRelease && (
        <ReleaseNotePopup
          release={latestRelease}
          onClose={() => setShouldShowPopup(false)}
        />
      )}
      <LocationTracker />
      <AppRoutes />
    </AblyProvider>
  );
}

export default AblyWrapper;
